import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { api } from '../services/api';

// ── Blot de imagem INLINE (permite imagens lado a lado no mesmo parágrafo) ──
// Guard: registrar apenas uma vez, mesmo em HMR
if (!(Quill as any).__inlineImageBlotRegistered) {
  const Embed = Quill.import('blots/embed') as any;

  class InlineImageBlot extends Embed {
    static blotName = 'image';
    static tagName = 'img';

    static create(value: string | { src: string; style?: string }) {
      const node = super.create() as HTMLImageElement;
      const src = typeof value === 'object' ? value.src : value;
      const style = typeof value === 'object' ? value.style : undefined;
      node.setAttribute('src', src);
      node.setAttribute('alt', '');
      if (style) {
        node.setAttribute('style', style);
      } else {
        node.style.verticalAlign = 'middle';
        node.style.maxWidth = '100%';
        node.style.height = 'auto';
      }
      return node;
    }

    static value(node: HTMLImageElement) {
      const src = node.getAttribute('src') || '';
      const style = node.getAttribute('style') || '';
      return style ? { src, style } : src;
    }
  }

  Quill.register(InlineImageBlot, true);
  (Quill as any).__inlineImageBlotRegistered = true;
}

// ── Tipos ────────────────────────────────────────────────────────────────────
interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  height?: string;
  className?: string;
  enableImages?: boolean;
}

interface ImageTooltip {
  img: HTMLImageElement;
  top: number;
  left: number;
  above: boolean;
}

const SIZE_PRESETS = [
  { label: '25%', value: '25%' },
  { label: '33%', value: '33%' },
  { label: '50%', value: '50%' },
  { label: '75%', value: '75%' },
  { label: '100%', value: '100%' },
];

// ── Componente ───────────────────────────────────────────────────────────────
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Digite o conteúdo...",
  readOnly = false,
  height = "300px",
  className = "",
  enableImages = false,
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Modal: imagem por link
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const savedRangeRef = useRef<{ index: number; length: number } | null>(null);

  // Tooltip: redimensionar imagem ao clicar
  const [tooltip, setTooltip] = useState<ImageTooltip | null>(null);

  // ── Imagem por link ───────────────────────────────────────────────────────
  const openImageModal = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) savedRangeRef.current = editor.getSelection();
    setImageUrl('');
    setImageModalOpen(true);
  }, []);

  const confirmImageUrl = useCallback(() => {
    const url = imageUrl.trim();
    if (!url) return;
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = savedRangeRef.current ?? { index: editor.getLength(), length: 0 };
      editor.insertEmbed(range.index, 'image', url);
      editor.setSelection(range.index + 1, 0);
    }
    setImageModalOpen(false);
    setImageUrl('');
  }, [imageUrl]);

  // ── Upload de arquivo ─────────────────────────────────────────────────────
  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('imagem', file);
      try {
        const response = await api.post('/uploads/imagem', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url: string = response.data.url;
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1, 0);
        }
      } catch { /* silently fail */ }
    };
  }, []);

  // ── Menu do botão de imagem (upload ou link) ──────────────────────────────
  const handleImageToolbar = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) savedRangeRef.current = editor.getSelection();

    const existing = document.getElementById('ql-image-menu');
    if (existing) { existing.remove(); return; }

    const toolbar = (quillRef.current?.getEditor() as any)?.getModule('toolbar')?.container as HTMLElement | null;
    const btn = toolbar?.querySelector('.ql-image') as HTMLElement | null;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const menu = document.createElement('div');
    menu.id = 'ql-image-menu';
    menu.style.cssText = `
      position:fixed;top:${rect.bottom + 4}px;left:${rect.left}px;
      background:#fff;border:1px solid #e5e7eb;border-radius:6px;
      box-shadow:0 4px 12px rgba(0,0,0,0.12);z-index:99999;min-width:160px;overflow:hidden;
    `;
    const itemStyle = `display:block;width:100%;padding:8px 14px;text-align:left;
      font-size:13px;cursor:pointer;background:none;border:none;color:#374151;font-family:inherit;`;

    const btnUpload = document.createElement('button');
    btnUpload.textContent = '📁  Enviar arquivo';
    btnUpload.style.cssText = itemStyle;
    btnUpload.onmouseenter = () => { btnUpload.style.background = '#f3f4f6'; };
    btnUpload.onmouseleave = () => { btnUpload.style.background = 'none'; };
    btnUpload.onclick = () => { menu.remove(); handleImageUpload(); };

    const btnLink = document.createElement('button');
    btnLink.textContent = '🔗  Inserir por link';
    btnLink.style.cssText = itemStyle;
    btnLink.onmouseenter = () => { btnLink.style.background = '#f3f4f6'; };
    btnLink.onmouseleave = () => { btnLink.style.background = 'none'; };
    btnLink.onclick = () => { menu.remove(); openImageModal(); };

    menu.appendChild(btnUpload);
    menu.appendChild(btnLink);
    document.body.appendChild(menu);

    const close = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) { menu.remove(); document.removeEventListener('mousedown', close); }
    };
    setTimeout(() => document.addEventListener('mousedown', close), 0);
  }, [handleImageUpload, openImageModal]);

  // ── Cleanup do menu flutuante de imagem ao desmontar ──────────────────────
  useEffect(() => {
    return () => {
      document.getElementById('ql-image-menu')?.remove();
    };
  }, []);

  // ── Tooltip de redimensionamento ao clicar em imagem ─────────────────────
  useEffect(() => {
    if (readOnly) return;
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG' && container.contains(target)) {
        e.preventDefault();
        const rect = target.getBoundingClientRect();
        const above = rect.top >= 50;
        setTooltip({
          img: target as HTMLImageElement,
          top: above ? rect.top - 44 : rect.bottom + 6,
          left: Math.max(4, rect.left),
          above,
        });
      } else if (!((e.target as HTMLElement)?.closest?.('#ql-img-tooltip'))) {
        setTooltip(null);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [readOnly]);

  const applyImageSize = useCallback((pct: string) => {
    if (!tooltip?.img) return;
    tooltip.img.style.width = pct;
    tooltip.img.style.maxWidth = pct;
    tooltip.img.style.height = 'auto';
    const editor = quillRef.current?.getEditor();
    if (editor) onChange(editor.root.innerHTML);
    setTooltip(null);
  }, [tooltip, onChange]);

  // ── Modules ───────────────────────────────────────────────────────────────
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link'],
        ...(enableImages ? [['image']] : []),
        ['clean'],
      ],
      handlers: {
        ...(enableImages ? { image: handleImageToolbar } : {}),
      },
    },
  }), [enableImages, handleImageToolbar]);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'script',
    'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'color', 'background',
    'link',
    ...(enableImages ? ['image'] : []),
  ];

  return (
    <div ref={containerRef} className={`rich-text-editor ${className}`} style={{ position: 'relative' }}>
      <style>{`
        .rich-text-editor .ql-editor {
          min-height: ${height};
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }

        .rich-text-editor .ql-toolbar {
          border-top: 1px solid #e5e7eb;
          border-left: 1px solid #e5e7eb;
          border-right: 1px solid #e5e7eb;
          border-bottom: none;
          background: #f9fafb;
        }

        .rich-text-editor .ql-container {
          border-bottom: 1px solid #e5e7eb;
          border-left: 1px solid #e5e7eb;
          border-right: 1px solid #e5e7eb;
          border-top: none;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          opacity: 1;
        }

        .rich-text-editor .ql-editor:focus {
          outline: none;
        }

        .rich-text-editor .ql-container.ql-snow {
          border-radius: 0 0 6px 6px;
        }

        .rich-text-editor .ql-toolbar.ql-snow {
          border-radius: 6px 6px 0 0;
        }

        /* Imagens inline — permitem ficar lado a lado */
        .rich-text-editor .ql-editor img {
          display: inline-block;
          max-width: 100%;
          height: auto;
          vertical-align: middle;
          border-radius: 4px;
          margin: 2px 3px;
          cursor: pointer;
          transition: outline 0.1s;
        }

        .rich-text-editor .ql-editor img:hover {
          outline: 2px solid #93c5fd;
          outline-offset: 1px;
        }

        .rich-text-editor .ql-editor .ql-align-center img {
          display: inline-block;
        }

        .rich-text-editor .ql-color-picker .ql-picker-options,
        .rich-text-editor .ql-color-picker.ql-expanded .ql-picker-options {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 4px;
          z-index: 9999;
          position: absolute;
        }

        .rich-text-editor .ql-color-picker .ql-picker-item {
          border: 1px solid transparent;
          border-radius: 2px;
        }

        .rich-text-editor .ql-color-picker .ql-picker-item:hover {
          border-color: #6b7280;
        }

      `}</style>

      {/* Tooltip de redimensionamento de imagem */}
      {tooltip && (
        <div
          id="ql-img-tooltip"
          style={{
            position: 'fixed',
            top: tooltip.top,
            left: tooltip.left,
            zIndex: 99999,
            background: '#1f2937',
            borderRadius: '6px',
            padding: '5px 8px',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            pointerEvents: 'all',
          }}
        >
          <span style={{ color: '#9ca3af', fontSize: '11px', marginRight: '2px', whiteSpace: 'nowrap' }}>
            Largura:
          </span>
          {SIZE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onMouseDown={(e) => { e.preventDefault(); applyImageSize(preset.value); }}
              style={{
                background: tooltip.img.style.width === preset.value ? '#3b82f6' : '#374151',
                border: 'none',
                borderRadius: '4px',
                color: '#f9fafb',
                fontSize: '11px',
                fontWeight: 500,
                padding: '3px 7px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { if (tooltip.img.style.width !== preset.value) (e.currentTarget).style.background = '#4b5563'; }}
              onMouseLeave={(e) => { if (tooltip.img.style.width !== preset.value) (e.currentTarget).style.background = '#374151'; }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        modules={modules}
        formats={formats}
      />

      {/* Modal de inserção de imagem por link */}
      {imageModalOpen && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setImageModalOpen(false); }}
        >
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Inserir imagem por link</h3>
            <input
              type="url"
              autoFocus
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://exemplo.com/imagem.png"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmImageUrl(); if (e.key === 'Escape') setImageModalOpen(false); }}
            />
            <div className="flex justify-end gap-2">
              <button
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                onClick={() => setImageModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={!imageUrl.trim()}
                onClick={confirmImageUrl}
              >
                Inserir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
